import { useEffect, useRef, useState } from "react";
import Lightbox from "../ui/Lightbox";
import { Chevron } from "../ui/Icons";
import { trackOnce } from "../track";
import type { LeadKind } from "../types";
import type { Words } from "./copy";

/** Every picture in ./proof, by file name: "<key>-<preview|real>-<sm|lg>.webp". */
const SHOTS = import.meta.glob<string>("./proof/*.webp", { eager: true, import: "default" });

/** Someone else's preview beside the piece it became.
 *
 *  Everything above this on the page is a picture on a screen, and the price
 *  underneath it is for a thing in the hand. The mail says the printed one comes
 *  out better than the render; this is where that stops being our word for it.
 *  The pairs are orders from the order cards on minimewonder.pt
 *  (data/stories.json, gallery: true), which people have already agreed to be
 *  shown, copied from its assets.
 *
 *  Only their own product: a keychain buyer is deciding about a keychain, and a
 *  couple's figurine says nothing about how a 4 cm piece on a ring comes out.
 *  There is no head keychain among the orders yet, so a head keychain is shown
 *  the pet keychains, the same ring and the same size, until there is one. */
interface Pair {
  key: string;
  preview: { sm: string; lg: string };
  real: { sm: string; lg: string };
}

/** A pair whose four pictures are all there, or null: a file gone missing
 *  costs that one pair, never the page. `real` names the piece's photo when it
 *  is not "<key>-real" (the parents' is the second one, as on the site). */
function pair(key: string, real = `${key}-real`): Pair | null {
  const [pSm, pLg, rSm, rLg] = [`${key}-preview-sm`, `${key}-preview-lg`, `${real}-sm`, `${real}-lg`].map(
    (name) => SHOTS[`./proof/${name}.webp`]
  );
  if (!pSm || !pLg || !rSm || !rLg) return null;
  return { key, preview: { sm: pSm, lg: pLg }, real: { sm: rSm, lg: rLg } };
}

function pairs(...list: (Pair | null)[]): Pair[] {
  return list.filter((p): p is Pair => p !== null);
}

// In the order the site's order cards have them.
const FIGURINES = pairs(pair("girl-cat"), pair("lynx"), pair("cucadog"), pair("kittens"));
const PAIRS = pairs(pair("couple", "couple-real2"), pair("couplecats"), pair("cousins"));
const KEYCHAINS = pairs(
  pair("pom"),
  pair("peke"),
  pair("terrier"),
  pair("klara"),
  pair("dog"),
  pair("luna"),
  pair("yorkie"),
  pair("rui2")
);

const ORDER: Record<LeadKind, Pair[]> = {
  minime: FIGURINES,
  duo: PAIRS,
  pet_keychain: KEYCHAINS,
  head_keychain: KEYCHAINS,
};

/** The pairs for a product, in the strip's order: the first is the one the
 *  hero card shows beside the drawing, so the page's two "this is how it
 *  prints" pictures are the same picture. */
export function pairsFor(kind: LeadKind): Pair[] {
  return ORDER[kind] ?? ORDER.minime;
}

export type { Pair as ProofPair };

export default function Proof({ kind, words: t }: { kind: LeadKind; words: Words }) {
  const shown = pairsFor(kind);
  const [zoomed, setZoomed] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Whether anyone gets this far is the question the section exists to answer,
  // so it is counted the same way the price is.
  useEffect(() => {
    const node = ref.current;
    if (!node || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) trackOnce("section.seen", { id: "proof" });
      },
      { threshold: 0.5 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Stepped through as they sit on the page: each preview, then what it became.
  const all = shown.flatMap((pair) => [pair.preview.lg, pair.real.lg]);
  const open = (src: string) => {
    setZoomed(src);
    trackOnce("photo.opened", { which: "proof" });
  };

  if (!shown.length) return null;

  return (
    <div className="card proof" ref={ref}>
      <h2>{t.t("proofTitle")}</h2>
      <p className="small muted" style={{ margin: 0 }}>
        {t.t("proofNote")}
      </p>

      <div className="proof-strip">
        {shown.map((pair) => (
          <div className="proof-pair" key={pair.key}>
            <button className="proof-shot" onClick={() => open(pair.preview.lg)}>
              <img src={pair.preview.sm} alt={t.t("proofPreview")} width={420} height={560} loading="lazy" />
              <span className="proof-tag">{t.t("proofPreview")}</span>
            </button>
            <span className="proof-arrow" aria-hidden="true">
              <Chevron size={14} />
            </span>
            <button className="proof-shot" onClick={() => open(pair.real.lg)}>
              <img src={pair.real.sm} alt={t.t("proofReal")} width={420} height={560} loading="lazy" />
              <span className="proof-tag proof-tag-real">{t.t("proofReal")}</span>
            </button>
          </div>
        ))}
      </div>

      {zoomed && <Lightbox src={zoomed} list={all} onClose={() => setZoomed(null)} />}
    </div>
  );
}
